/**
 * useChatSend — CUADRO NEGRO División Pragmática App.js
 *
 * Centraliza la función sendMessage() del chat. Mantiene EXACTAMENTE el
 * comportamiento previo: optimistic update, vision_mode, active_module_id,
 * detected_profile, active_modules, chart/diagram, internal_module_action,
 * workspace_action, propagación de image_id al assistantMessage local, y
 * los toasts de error (límites, 429 upgrade, 401, 404, 400 limit_error).
 *
 * Patrón pragmático autorizado por el CUADRO NEGRO: recibe setters y
 * dependencias del componente Home() como argumentos. NO duplica lógica.
 *
 * Exports: { sendMessage }
 */
import { useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { navigateToPremium } from '../../utils/navigateToPremium';
import { API_URL } from '../../config/api';
import { detectReviewIntent } from '../../utils/reviewIntent';
import { detectProjectIntent } from '../../utils/projectIntent';
import { detectGithubUrl } from '../../utils/githubIntent';
import useTrackFirstAction from '../useTrackFirstAction';

const API = API_URL;

/**
 * 🟥 P26.36 · Recupera el resultado de un turno cuya conexión se cortó.
 * Sondea `GET /chat/result/{id}` hasta ~2.5 min. Devuelve el payload del
 * chat o null si nunca quedó listo.
 */
const recoverChatResult = async (clientRequestId, attempts = 50, delayMs = 3000) => {
  for (let i = 0; i < attempts; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, delayMs));
    try {
      // eslint-disable-next-line no-await-in-loop
      const res = await axios.get(`${API}/chat/result/${clientRequestId}`);
      if (res.data?.status === 'done' && res.data?.data) return res.data.data;
    } catch (err) {
      if (err.response?.status === 404) return null;
    }
  }
  return null;
};

export default function useChatSend({
  token,
  language,
  currentConversation,
  setCurrentConversation,
  createNewConversation,
  selectConversation,
  loadConversations,
  activeModuleId,
  setCurrentProfile,
  setStyleWasManual,
  setLevelWasManual,
  setActiveModules,
  setCurrentChartData,
  setForceInternalWorkspace,
}) {
  const trackFirstAction = useTrackFirstAction();
  const sendMessage = useCallback(async (message, options = {}) => {
    let convId = currentConversation?.id;
    let newlyCreatedConv = null;

    if (!convId) {
      newlyCreatedConv = await createNewConversation();
      convId = newlyCreatedConv?.id;
      if (!convId) return;
    }

    // OPTIMISTIC UPDATE: Mostrar el mensaje del usuario inmediatamente
    const userMessage = {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    };

    // Guardar mensajes previos para revertir si hay error
    const previousConversation = currentConversation || newlyCreatedConv;
    const previousMessages = previousConversation?.messages || [];

    // Actualizar la conversación local inmediatamente
    const convForUpdate = currentConversation || newlyCreatedConv;
    if (convForUpdate) {
      const updatedConv = {
        ...convForUpdate,
        messages: [...(convForUpdate.messages || []), userMessage],
      };
      setCurrentConversation(updatedConv);
    }

    try {
      const headers = {};  // P2.1 cookie-only: cookie httpOnly via axios.defaults

      // ============================================================
      // 🟥 CUADRO NEGRO V3 — Atajo "Importar desde GitHub".
      // Si el mensaje contiene una URL pública de GitHub, mostramos el
      // prompt de importación en lugar del flujo normal del chat. Tiene
      // prioridad sobre review/project porque es una operación distinta.
      // Si el usuario cancela, queda como mensaje del asistente y puede
      // reenviar otro mensaje sin la URL.
      // ============================================================
      if (!options.visionMode && !options.skipGithubOffer) {
        const gh = detectGithubUrl(message);
        if (gh.isGithub) {
          const assistantMessage = {
            role: 'assistant',
            content: language === 'es'
              ? 'Detecté un repositorio de GitHub. ¿Lo importo al Área de Trabajo?'
              : 'I detected a GitHub repository. Import it to the Workspace?',
            timestamp: new Date().toISOString(),
            project_github_offer: {
              url: gh.url,
              owner: gh.owner,
              repo: gh.repo,
              branch: gh.branch || null,
            },
          };
          setCurrentConversation((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              messages: [...(prev.messages || []), assistantMessage],
            };
          });
          trackFirstAction('chat_message');
          return assistantMessage.content;
        }
      }

      // ============================================================
      // 🟥 CUADRO NEGRO — Atajo "Revisión estructurada".
      // Si el mensaje del usuario contiene keywords de revisión y NO viene
      // con vision_mode (que tiene su propio short-circuit en el backend),
      // ruteamos a /api/review/chat. Devuelve { type:"review", title,
      // sections, rawText, conversation_id } que persistimos en el
      // assistantMessage como `review` para que MessageBubble renderice
      // <ReviewBox/>. No afecta al flujo normal de /api/chat para mensajes
      // que no son de revisión.
      // ============================================================
      if (!options.visionMode && detectReviewIntent(message)) {
        try {
          const reviewResp = await axios.post(`${API}/review/chat`, {
            message,
            conversation_id: convId,
            language,
          }, { headers });

          trackFirstAction('chat_message');

          if (reviewResp.data?.type === 'review') {
            const assistantMessage = {
              role: 'assistant',
              content: reviewResp.data.rawText || '',
              timestamp: new Date().toISOString(),
              review: {
                type: 'review',
                title: reviewResp.data.title,
                sections: reviewResp.data.sections || [],
                rawText: reviewResp.data.rawText || '',
              },
            };

            setCurrentConversation((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                messages: [...(prev.messages || []), assistantMessage],
              };
            });

            // Recargar conversación desde DB (el backend persistió user+assistant
            // con metadata `review`).
            const persistedConvId = reviewResp.data.conversation_id || convId;
            if (persistedConvId) {
              await selectConversation(persistedConvId);
              await loadConversations();
            }

            return reviewResp.data.rawText;
          }
          // Si type !== "review" (no detectado server-side), caemos al flujo
          // normal de /api/chat más abajo.
        } catch (revErr) {
          // Si el endpoint de review falla por cualquier motivo, hacemos
          // graceful fallback al chat normal en lugar de romper el envío.
          console.warn('[review/chat] fallback to /chat:', revErr?.response?.status || revErr?.message);
        }
      }

      // ============================================================
      // 🟥 CUADRO NEGRO — Atajo "Proyecto en código".
      // Si detectProjectIntent() puntúa fuerte sobre el mensaje y NO es
      // vision_mode, ofrecemos al usuario adaptar el proyecto al Área de
      // Trabajo en lugar de mandar al chat normal. Inyectamos un mensaje
      // del asistente con `project_offer` y terminamos: el usuario decide
      // con los botones del ProjectAdaptPrompt si seguir o no. Si declina,
      // puede reenviar el mismo mensaje (la oferta no se duplica porque la
      // detección sigue siendo positiva, pero el componente local recuerda
      // el estado 'declined'). Si acepta, el componente llama /api/projects/build.
      // ============================================================
      if (!options.visionMode && !options.skipProjectOffer) {
        const projectInfo = detectProjectIntent(message);
        if (projectInfo.isProject) {
          try {
            // Llamamos a /api/projects/analyze para refinar metadata
            // server-side (lenguaje, sugerencia de nombre). Si falla, usamos
            // los valores locales como fallback — la oferta se muestra igual.
            let serverInfo = {};
            try {
              const ar = await axios.post(`${API}/projects/analyze`, { message }, { headers });
              if (ar.data?.is_project) serverInfo = ar.data;
            } catch (analyzeErr) {
              console.warn('[projects/analyze] using local fallback:', analyzeErr?.message);
            }

            const assistantMessage = {
              role: 'assistant',
              content: language === 'es'
                ? 'Detecté un proyecto en código. ¿Quieres que lo adapte al Área de Trabajo?'
                : 'I detected a code project. Want me to adapt it to the Workspace?',
              timestamp: new Date().toISOString(),
              project_offer: {
                rawMessage: message,
                suggestedName: serverInfo.suggested_name || projectInfo.strongMarkers?.[0] || null,
                language: serverInfo.language || null,
                fileCountEstimate: serverInfo.file_count_estimate || projectInfo.fenceCount || 0,
              },
            };

            setCurrentConversation((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                messages: [...(prev.messages || []), assistantMessage],
              };
            });

            trackFirstAction('chat_message');
            return assistantMessage.content;
          } catch (projErr) {
            console.warn('[projects offer] fallback to /chat:', projErr?.message);
          }
        }
      }

      // Get user style preferences from localStorage
      const stylePreference = localStorage.getItem('damyxia_style');
      const levelPreference = localStorage.getItem('damyxia_level');

      const requestBody = {
        conversation_id: convId,
        message: message,
        language: language,
      };

      // Vision mode: si el caller adjunta una imagen base64, la enviamos al
      // backend con vision_mode=true para que GPT-4o lea el contenido.
      if (options.visionMode && options.imageBase64) {
        requestBody.vision_mode = true;
        requestBody.image_base64 = options.imageBase64;
        if (options.mimeType) requestBody.mime_type = options.mimeType;
      }

      // FASE 11: Workspace ↔ Chat sync.
      // Si hay un módulo abierto en el InternalWorkspace, lo pasamos al backend
      // para que opere SOBRE él en lugar de crear uno nuevo cuando la intención
      // del mensaje es ambigua. Backend valida owner antes de aceptarlo.
      if (activeModuleId) {
        requestBody.active_module_id = activeModuleId;
      }

      // P22 · Clasificador A/B — el backend decide si abrir builder o solo
      // responder texto. `has_active_project` lo decide el frontend: true si
      // hay un módulo abierto. `force_category` se rellena SOLO cuando el
      // usuario ya eligió con los botones inline ("Aplicar" / "Solo mostrar").
      requestBody.has_active_project = Boolean(activeModuleId);
      if (options.forceCategory === 'A' || options.forceCategory === 'B') {
        requestBody.force_category = options.forceCategory;
      }

      if (stylePreference && stylePreference !== 'auto') {
        requestBody.style_preference = stylePreference;
      }
      if (levelPreference && levelPreference !== 'auto') {
        requestBody.level_preference = levelPreference;
      }

      // 🟩 P26.6 · Builder SIEMPRE ACTIVO por mandato del owner.
      // Toggle eliminado del frontend. Enviamos "ON" en toda request para
      // que el backend inyecte permanentemente BUILDER_ON_ALWAYS_REMINDER
      // cuando el usuario use verbos de creación.
      requestBody.builder_toggle = 'ON';
      try {
        window.localStorage.setItem('damyxia_builder_global', 'ON');
      } catch (_) { /* ignore */ }

      // 🟥 P26.36 · Turno recuperable. Si la conexión se corta (proxy,
      // recarga del preview, red móvil), el backend igual termina el trabajo
      // y lo dejamos recuperar por id en vez de perderlo.
      const clientRequestId = (window.crypto?.randomUUID?.()
        || `crid-${Date.now()}-${Math.random().toString(36).slice(2)}`);
      requestBody.client_request_id = clientRequestId;

      let response;
      try {
        response = await axios.post(`${API}/chat`, requestBody, { headers });
      } catch (netErr) {
        const recoverable = !netErr.response
          || netErr.code === 'ECONNABORTED'
          || (netErr.response?.status >= 500);
        if (!recoverable) throw netErr;
        toast.loading(
          language === 'es'
            ? 'Se cortó la conexión — recuperando la respuesta…'
            : 'Connection dropped — recovering the answer…',
          { id: clientRequestId, duration: 150000 }
        );
        const recovered = await recoverChatResult(clientRequestId);
        toast.dismiss(clientRequestId);
        if (!recovered) throw netErr;
        response = { data: recovered };
        toast.success(
          language === 'es' ? 'Respuesta recuperada.' : 'Answer recovered.',
          { duration: 4000 }
        );
      }

      // ORB — registrar primera acción útil (best-effort, idempotente).
      trackFirstAction('chat_message');


      // 🟥 P26.45 · Orden de apertura ("volvé a abrir MarAzul"): el backend
      // resuelve el trabajo y acá se abre el Workspace en ese proyecto.
      if (response.data.open_module_id) {
        try {
          window.dispatchEvent(new Event('damyxia:open-internal-workspace'));
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('damyxia-open-internal-module', {
              detail: {
                moduleId: response.data.open_module_id,
                name: response.data.open_module_name,
                source: 'chat_open_intent',
              },
            }));
          }, 150);
        } catch (_) { /* noop */ }
      }

      // Update current profile from response
      if (response.data.detected_profile) {
        setCurrentProfile(response.data.detected_profile);
        setStyleWasManual(response.data.style_was_manual || false);
        setLevelWasManual(response.data.level_was_manual || false);
      }

      // Update active modules indicator
      if (response.data.active_modules) {
        setActiveModules(response.data.active_modules);
      } else {
        setActiveModules([]);
      }

      // Update chart data if present in response
      if (response.data.has_chart && response.data.chart_data) {
        setCurrentChartData(response.data.chart_data);
      } else if (response.data.has_diagram && response.data.diagram_code) {
        setCurrentChartData({
          type: 'mermaid',
          title: 'Diagrama',
          mermaid_code: response.data.diagram_code,
        });
      }

      // Handle internal module actions
      if (response.data.internal_module_action) {
        const action = response.data.internal_module_action;
        const moduleName = response.data.internal_module_name;
        const moduleIdResp = response.data.internal_module_id;

        // FASE 12 ULTRA: edit destructivo bloqueado por el backend.
        if (action === 'edit_rejected') {
          toast.warning(
            language === 'es'
              ? '⚠️ Edición bloqueada: la propuesta eliminaba contenido. Tu trabajo quedó intacto.'
              : '⚠️ Edit blocked: proposal removed content. Your work is intact.',
            { duration: 6000 }
          );
        }

        // FASE 11: Si el chat editó/creó un módulo, notificar al Workspace
        // para que recargue la vista en vivo si es el módulo activo.
        if (moduleIdResp && (action === 'editing' || action === 'edited' || action === 'created' || action === 'opened')) {
          try {
            window.dispatchEvent(new CustomEvent('damyxia:internal-module-updated', {
              detail: { moduleId: moduleIdResp, action },
            }));
          } catch (_) { /* noop */ }

          // 🟥 FIX 2026-02-10 · Bug "salta a Edición sin consolidar creación":
          // Al CREAR (o REABRIR) un módulo, el handler `internal-module-updated`
          // solo re-selecciona lo que YA está seleccionado. Sin este dispatch
          // extra, el módulo recién creado nunca queda como `activeModule`, y
          // el turno #2 termina editando otro trabajo (o ninguno). Emitimos
          // el evento `damyxia-open-internal-module` que fuerza al Workspace
          // a hacer fetch + setSelectedModule del id nuevo, con lo cual el
          // próximo mensaje del chat lleva `active_module_id` correcto.
          //
          // 🟥 iter432 · Regla IV.2 del owner: "el Workspace debe abrirse
          // automáticamente en CADA creación/edición". Agregamos también
          // `edited` para el caso en que el chat edite un módulo que el
          // usuario no tenía abierto (edición por nombre, comando remoto).
          if (action === 'created' || action === 'opened' || action === 'edited') {
            try {
              window.dispatchEvent(new Event('damyxia:open-internal-workspace'));
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('damyxia-open-internal-module', {
                  detail: { moduleId: moduleIdResp, source: 'chat_' + action },
                }));
              }, 120);
            } catch (_) { /* noop */ }
          }
        }

        const messageLower = message.toLowerCase();
        const wantsToOpenInWorkspace =
          messageLower.includes('área de trabajo') ||
          messageLower.includes('area de trabajo') ||
          messageLower.includes('al workspace') ||
          messageLower.includes('en el workspace') ||
          messageLower.includes('in workspace') ||
          messageLower.includes('to workspace');

        if (action === 'created') {
          if (wantsToOpenInWorkspace) {
            toast.success(
              language === 'es'
                ? `Trabajo "${moduleName}" creado y abierto en el Área de Trabajo.`
                : `Work "${moduleName}" created and opened in Workspace.`,
              { duration: 3000 }
            );
            setForceInternalWorkspace(true);
          } else {
            toast.success(
              language === 'es'
                ? `Trabajo "${moduleName}" creado. Abrilo en el Área Interna de Trabajo.`
                : `Work "${moduleName}" created. Open it in Internal Workspace.`,
              {
                duration: 5000,
                action: {
                  label: language === 'es' ? 'Abrir' : 'Open',
                  onClick: () => {
                    setForceInternalWorkspace(true);
                  },
                },
              }
            );
          }
        } else if (action === 'edited') {
          toast.success(
            language === 'es'
              ? `Cambios guardados en "${moduleName}".`
              : `Changes saved to "${moduleName}".`,
            { duration: 3000 }
          );
          if (wantsToOpenInWorkspace) {
            setForceInternalWorkspace(true);
          }
        } else if (action === 'opened') {
          toast.info(
            language === 'es'
              ? `Abriendo "${moduleName}"...`
              : `Opening "${moduleName}"...`,
            { duration: 2000 }
          );
          setForceInternalWorkspace(true);
        }
      }

      // Handle workspace image publish action
      if (response.data.workspace_action === 'published') {
        window.dispatchEvent(new CustomEvent('workspace-image-published', {
          detail: {
            workspaceItem: response.data.workspace_item,
            imageId: response.data.workspace_image_id,
          },
        }));
        toast.success(
          language === 'es'
            ? '✅ Imagen publicada en el Área de Trabajo'
            : '✅ Image published to Workspace',
          { duration: 3000 }
        );
      }

      // IMMEDIATE UPDATE: Add assistant response to conversation instantly
      // This ensures the workspace updates without waiting for selectConversation
      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString(),
      };

      if (response.data.has_image && response.data.image_data) {
        assistantMessage.image_base64_preview = response.data.image_data;
      }

      // CUADRO NEGRO — Si VISIÓN generó una imagen, propagar image_id e
      // image_url al assistantMessage para que el botón "Enviar al área de
      // trabajo" tenga payload válido. Sin esto, message.image_id queda
      // undefined → handleSendToWorkspace cae al fallback de texto y abre
      // el workspace vacío.
      const visionImageId = response.data.image_id || response.data.workspace_image_id;
      if (visionImageId) {
        assistantMessage.image_id = visionImageId;
        assistantMessage.image_url = response.data.workspace_image_url || `/api/workspace/image/${visionImageId}`;
        assistantMessage.has_image = true;
      }

      // P22 · Intent A/B — si el backend devolvió AMBIGUOUS, propagamos el
      // prompt inline al assistantMessage para que ChatMessage renderice
      // los dos botones ("Aplicar en el proyecto" / "Solo mostrar aquí").
      if (response.data.intent_prompt) {
        assistantMessage.intent_prompt = response.data.intent_prompt;
        assistantMessage.intent_category = response.data.intent_category;
      } else if (response.data.intent_category) {
        assistantMessage.intent_category = response.data.intent_category;
      }
      // P23 · toggle_state ("ON" | "OFF" | null) para que el badge Solo Chat
      // se pueda ocultar cuando toggle=OFF (el toggle del header ya lo dice).
      if (response.data.toggle_state !== undefined) {
        assistantMessage.toggle_state = response.data.toggle_state;
      }

      // 🟥 2026-02-05 · Propagar metadata al mensaje optimista (audit screenshots).
      // Sin esto, AuditScreenshotsBlock nunca se renderiza porque el reload
      // desde DB puede llegar tarde o quedar en otra conversación.
      if (response.data.metadata && typeof response.data.metadata === 'object') {
        assistantMessage.metadata = response.data.metadata;
      }
      if (response.data.message_id) {
        assistantMessage.id = response.data.message_id;
      }

      setCurrentConversation((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: [...(prev.messages || []), assistantMessage],
        };
      });

      // Then reload full conversation from DB for accuracy (images, metadata, etc.)
      await selectConversation(convId);
      await loadConversations();

      return response.data.response || response.data.message;
    } catch (error) {
      // Revertir optimistic update en caso de error
      if (previousConversation) {
        setCurrentConversation({
          ...previousConversation,
          messages: previousMessages,
        });
      }

      // Handle specific errors with clear messages
      const detailObj = error.response?.data?.detail;
      const isLimitError = error.response?.status === 400 && typeof detailObj === 'object' && detailObj?.type === 'limit_error';
      if (isLimitError) {
        toast.error(
          detailObj.message ||
            (language === 'es'
              ? 'Límite superado — máximo permitido: 25.000 palabras por mensaje.'
              : 'Limit exceeded — maximum allowed: 25,000 words per message.'),
          {
            duration: 5000,
            style: { background: '#2b2b2b', color: '#ff6b6b' },
          }
        );
      } else if (error.response?.status === 429) {
        const detail = error.response?.data?.detail;
        const upgradeRequired = typeof detail === 'object' && detail.upgrade_required;
        let errorMsg;
        if (upgradeRequired) {
          errorMsg = language === 'es'
            ? 'Has alcanzado tu límite. Actualiza a Premium para uso ilimitado.'
            : 'You have reached your limit. Upgrade to Premium for unlimited use.';
        } else {
          errorMsg = language === 'es' ? 'Límite de mensajes alcanzado' : 'Message limit reached';
        }
        const toastOpts = { duration: 7000 };
        if (upgradeRequired) {
          toastOpts.action = {
            label: language === 'es' ? 'Actualizar' : 'Upgrade',
            onClick: navigateToPremium,
          };
        }
        toast.error(errorMsg, toastOpts);
      } else if (error.response?.status === 401) {
        toast.error(language === 'es' ? 'Sesión expirada. Por favor, inicia sesión nuevamente.' : 'Session expired. Please log in again.');
      } else if (error.response?.status === 404) {
        toast.error(language === 'es' ? 'Conversación no encontrada. Crea una nueva.' : 'Conversation not found. Create a new one.');
      } else if (!error.response || error.code === 'ECONNABORTED' || error.response?.status >= 500) {
        // 🟥 P26.34 — 5xx del proxy (520/524/502) o corte de conexión: el
        // pedido tardó demasiado o el servidor no respondió. Mensaje claro
        // en vez del HTML crudo de Cloudflare.
        toast.error(
          language === 'es'
            ? 'El pedido tardó demasiado y se cortó la conexión. Probá de nuevo con una orden más corta o dividida en 2 pasos.'
            : 'The request took too long and the connection dropped. Try again with a shorter request or split it in 2 steps.',
          { duration: 8000 }
        );
      } else {
        const detail = error.response?.data?.detail;
        const errorMsg = typeof detail === 'string'
          ? detail
          : (language === 'es' ? 'Error al enviar mensaje. Intenta de nuevo.' : 'Error sending message. Try again.');
        toast.error(errorMsg);
      }

      throw error;
    }
  }, [
    token,
    language,
    currentConversation,
    setCurrentConversation,
    createNewConversation,
    selectConversation,
    loadConversations,
    activeModuleId,
    setCurrentProfile,
    setStyleWasManual,
    setLevelWasManual,
    setActiveModules,
    setCurrentChartData,
    setForceInternalWorkspace,
    trackFirstAction,
  ]);

  return { sendMessage };
}
